import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  Control,
  FieldValues,
  FieldPath,
  FieldErrors,
  UseControllerProps,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import Select, { Option } from '@ingka/select';

import { getter } from '@/lib/utils/object';
import { extractErrorMessage } from '../error-message';

import styles from './index.module.css';

type DropdownItem = {
  value: string;
  i18nKey: string;
};

export type ControlledInputProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: FieldPath<T>;
  control: Control<T, any>;
  errors: FieldErrors<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  // Ingka form field props
  readOnly?: boolean;
  items: DropdownItem[];
  label: string;
  shouldValidate?: boolean;
  successMessage?: string;
};

export function ControlledDropdown<T extends FieldValues>(
  props: PropsWithChildren<ControlledInputProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    control,
    errors,
    rules,
    shouldValidate = true,
    label,
    successMessage,
    forcedError,
    items,
    readOnly,
  } = props;
  // Error handling
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  return (
    <div className={props.className}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <FormField
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate && error}
          >
            <Select
              {...field}
              id={name}
              label={label}
              value={field.value}
              disabled={readOnly}
              className={styles.input}
            >
              {items.map(({ value, i18nKey }) => (
                <Option key={value} value={value} name={t(i18nKey)} />
              ))}
            </Select>
          </FormField>
        )}
      />
    </div>
  );
}

export default ControlledDropdown;
